import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { observer } from 'mobx-react-lite';
import { Box, Button, Flex, Text } from '@cad-arc';
import { logDebug } from '../monitoring/log';
import { SENSOR_HLS_BACK_BUFFER_SECONDS } from './constants';
import { formatSensorAlertClockTime, formatSensorDistance, NearbySensor } from './helpers';
import { useOpenSensorPanel } from './use-open-sensor-panel';
import { useSensorsStore } from './use-sensors-store';
import { buildHlsUrl, useSensorStreamToken } from './use-sensor-stream-token';
import { VideoModeBadge } from './video-player-chrome';

/**
 * One tile's live feed: a bare video and nothing else.
 *
 * Deliberately not `CameraVideo` — nine copies of a full control bar in a 3 x 3 grid is noise, and
 * the design puts the transport on the panel a tile opens into, not on the tile.
 *
 * Nine concurrent HLS sessions is a real cost: nine network streams and nine hardware decodes in a
 * client that already runs for whole shifts. `resolution=low_res` is pinned server-side, which
 * keeps it survivable, but if the grid turns out to strain a dispatcher's machine the fix is to
 * play only the focused tile and show stills for the rest.
 */
function SensorGridTileFeed({
    sensorId,
    posterUrl,
}: {
    sensorId: string;
    posterUrl?: string;
}): React.ReactElement {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const { data: token } = useSensorStreamToken();
    const [hasPlaybackError, setHasPlaybackError] = useState(false);

    const hlsUrl = buildHlsUrl(token, sensorId);

    useEffect(
        function attachHlsToTile() {
            const videoElement = videoRef.current;
            if (!videoElement || !hlsUrl) {
                return;
            }
            setHasPlaybackError(false);

            if (!Hls.isSupported()) {
                if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                    videoElement.src = hlsUrl;
                    return;
                }
                setHasPlaybackError(true);
                return;
            }

            const hls = new Hls({
                enableWorker: true,
                backBufferLength: SENSOR_HLS_BACK_BUFFER_SECONDS,
            });
            hls.on(Hls.Events.ERROR, (_event, data) => {
                if (data.fatal) {
                    // Classification only: the URL carries the streaming JWT.
                    logDebug('sensor grid tile hls fatal error', {
                        errorType: data.type,
                        errorDetails: data.details,
                    });
                    setHasPlaybackError(true);
                    hls.destroy();
                }
            });
            hls.loadSource(hlsUrl);
            hls.attachMedia(videoElement);

            return function detachHls() {
                hls.destroy();
            };
        },
        [hlsUrl]
    );

    if (!hlsUrl || hasPlaybackError) {
        return (
            <Flex
                height="100%"
                alignItems="center"
                justifyContent="center"
                style={{
                    background: '#000000',
                    backgroundImage:
                        'repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 10px, transparent 10px 20px)',
                }}
            >
                <Text variant="caption" color="secondary" fontFamily="mono">
                    NO SIGNAL
                </Text>
            </Flex>
        );
    }

    return (
        // Hidden from Session Replay for the same reason as the sensor panel's player: the poster
        // is the alert image URL, a live credential, and replay records `poster` as-is.
        <video
            ref={videoRef}
            data-dd-privacy="hidden"
            autoPlay
            muted
            playsInline
            poster={posterUrl}
            style={{
                // Absolute so the feed fills its box, whose aspect ratio sets the tile's height
                // rather than the stream's own.
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                background: '#000000',
            }}
        />
    );
}

/**
 * One camera in the grid: feed, then a caption carrying what an overlay cannot — site, today's
 * event count, and the action.
 *
 * `rank` and `distance` come from the sensor's real position in the distance ordering, so a tile
 * repeated by the grid-filling hack still names the camera it actually is.
 */
export const SensorGridTile = observer(function SensorGridTile({
    sensor,
}: {
    sensor: NearbySensor;
}): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const openSensorPanel = useOpenSensorPanel();

    const alerts = sensorsStore.getAlertsForSensor(sensor.externalId);
    const latestAlert = alerts[0];
    const isLive = sensor.online;

    return (
        <Flex
            direction="column"
            borderRadius="md"
            overflow="hidden"
            backgroundColor="surface.foreground"
        >
            {/* A camera's frame shape, so the tile's height follows from its width. */}
            <Box position="relative" style={{ aspectRatio: '16 / 9' }}>
                <SensorGridTileFeed
                    sensorId={sensor.externalId}
                    posterUrl={latestAlert?.imageUrl}
                />
                <Box position="absolute" top={1} right={1} pointerEvents="none">
                    <VideoModeBadge mode={isLive ? 'live' : 'signalLost'} />
                </Box>
            </Box>

            <Flex direction="column" gap={1} padding={2} flexShrink={0}>
                <Flex alignItems="center" gap={2} minWidth={0}>
                    {/* Rank chip, so a row in the events rail can be traced back to its tile. */}
                    <Flex
                        width="18px"
                        height="18px"
                        flexShrink={0}
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="sm"
                        backgroundColor="selected.accent"
                    >
                        <Text variant="caption" fontFamily="mono" color="brand">
                            {sensor.rank}
                        </Text>
                    </Flex>
                    <Text variant="bodyMd" fontWeight="bold" noOfLines={1}>
                        {sensor.name ?? sensor.externalId}
                    </Text>
                    <Box marginLeft="auto" flexShrink={0}>
                        <Text variant="caption" fontFamily="mono" color="secondary">
                            {`${formatSensorDistance(sensor.distanceMeters)} ${sensor.bearing}`}
                        </Text>
                    </Box>
                </Flex>
                <Text variant="caption" color="secondary" noOfLines={1}>
                    {[
                        sensor.site,
                        alerts.length === 0
                            ? 'no recent events'
                            : `${alerts.length} recent event${alerts.length === 1 ? '' : 's'}`,
                        latestAlert
                            ? `last ${formatSensorAlertClockTime(latestAlert.createdDateUtc)}`
                            : undefined,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </Text>
                {/* Every tile gets the same treatment. The design makes the nearest camera's
                    action primary, but nine tiles with one solid button among eight outlines
                    just reads as inconsistent. */}
                <Button
                    size="sm"
                    trailingVisual="OpenNewWindow"
                    onClick={() => openSensorPanel({ sensorId: sensor.externalId })}
                >
                    Open sensor
                </Button>
            </Flex>
        </Flex>
    );
});
